/**
 * ComposeView — new support request form: lane segmented control (routes to
 * Org Admin vs UpskillABA Support), a type select for the platform lane,
 * subject/details, PHI warning, auto-captured context, attachments, and a
 * submit footer. Draft (lane/type/subject/body) autosaves to
 * `supportWidgetStore` so an accidental close doesn't lose it; a
 * `composePrefill` (from "Report this issue") wins over the draft's
 * lane/type/subject but keeps the draft body, per the plan's edge case.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Paperclip } from 'lucide-react';
import {
  LANE_SUPPORT_TYPES,
  SUPPORT_TYPE_LABEL,
  defaultSupportTypeForLane,
  type SupportLane,
  type SupportRequestType,
} from '@upskill/shared';
import { useSupportWidgetStore } from '../../../stores/supportWidgetStore';
import { useCreateSupportRequest } from '../../../hooks/useSupport';
import { captureSupportContext } from './AutoContextCard';
import { useSupportAttachments } from './useSupportAttachments';
import { PendingAttachmentChip } from './supportShared';

const DRAFT_SAVE_DEBOUNCE_MS = 400;

const Form = styled.form`
  padding-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ErrorBanner = styled.div`
  margin: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]} 0;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.info.bg};
  color: ${({ theme }) => theme.colors.status.info.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary.dark};
  margin: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[4]}
    ${({ theme }) => theme.spacing[2]};
`;

const Field = styled.div`
  margin: 0 ${({ theme }) => theme.spacing[4]};
`;

const TextInput = styled.input`
  width: 100%;
  border: 1.5px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const Select = styled.select`
  width: 100%;
  border: 1.5px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 84px;
  resize: vertical;
  border: 1.5px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  background: ${({ theme }) => theme.colors.backgrounds.white};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const Segmented = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin: 0 ${({ theme }) => theme.spacing[4]};
`;

const SegOption = styled.button<{ $selected: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1.5px solid
    ${({ theme, $selected }) => ($selected ? theme.colors.primary.base : theme.colors.borders.light)};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.backgrounds.light : theme.colors.backgrounds.white};
  cursor: pointer;
`;

const SegTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SegSub = styled.div`
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 2px;
`;

const HintNote = styled.div`
  margin: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[4]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.warning.text};
`;

const RouteHint = styled.div`
  margin: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]} 0;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.info.bg};
  color: ${({ theme }) => theme.colors.status.info.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const AttachRow = styled.div`
  margin: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]} 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const AttachTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.base};
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  padding: 0 ${({ theme }) => theme.spacing[4]};
`;

const SubmitButton = styled.button`
  width: 100%;
  background: ${({ theme }) => theme.colors.primary.base};
  color: ${({ theme }) => theme.colors.text.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DestNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.light};
`;

// ADO #542 item 4: announced via aria-live so a screen reader user hears WHY
// Send is disabled, not just that it is.
const DisabledReason = styled.p`
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.light};
`;

export interface ComposeViewProps {
  onSubmitted: (requestId: string) => void;
}

// A pasted log dump or copy-pasted email thread shouldn't land as a multi-MB
// blob in a @db.Text column and then in every admin inbox preview.
const MAX_BODY_LENGTH = 5000;

export function ComposeView({ onSubmitted }: ComposeViewProps) {
  const draft = useSupportWidgetStore((s) => s.draft);
  const setDraft = useSupportWidgetStore((s) => s.setDraft);
  const clearDraft = useSupportWidgetStore((s) => s.clearDraft);
  const composePrefill = useSupportWidgetStore((s) => s.composePrefill);
  const clearComposePrefill = useSupportWidgetStore((s) => s.clearComposePrefill);
  const createRequest = useCreateSupportRequest();

  // `lane` stays null until the user explicitly picks one — it must NOT
  // default to a real lane, or an untouched control silently routes
  // org-internal detail to whichever lane "won" the default. Both segment
  // cards and the RouteHint/DestNote copy render their neutral/unselected
  // state until then (ADO #542 item 5) — there is no cosmetic fallback lane.
  const [lane, setLane] = useState<SupportLane | null>(composePrefill?.lane ?? draft.lane ?? null);
  const [type, setType] = useState<SupportRequestType>(composePrefill?.type ?? draft.type);
  const [subject, setSubject] = useState(composePrefill?.subject ?? draft.subject);
  const [body, setBody] = useState(draft.body);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = composePrefill?.errorRef;

  // Picking a lane resets the type to that lane's catch-all so a stale
  // platform type can never be carried over and submitted on the account
  // lane (or vice versa) — the API rejects a type outside the request's lane
  // (ADO #542 item 3).
  const handleLaneChange = (next: SupportLane) => {
    setLane(next);
    setType(defaultSupportTypeForLane(next));
  };

  const {
    pending,
    addFiles,
    removeAttachment,
    handlePasteImage,
    handleDropFiles,
    readyAttachments,
    hasUploading,
    hasFailed,
  } = useSupportAttachments();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prefill (from "Report this issue") applies whenever it changes, not just
  // on mount — a second "Report this issue" click while compose is already
  // open must still land, not silently point support at the first error.
  useEffect(() => {
    if (!composePrefill) return;
    if (composePrefill.lane) setLane(composePrefill.lane);
    if (composePrefill.type) setType(composePrefill.type);
    if (composePrefill.subject) setSubject(composePrefill.subject);
    clearComposePrefill();
    // clearComposePrefill/setters are stable; only composePrefill identity should re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composePrefill]);

  // Debounced draft autosave so navigating away/closing the widget mid-compose
  // doesn't lose the in-progress message.
  useEffect(() => {
    const timer = setTimeout(() => setDraft({ lane, type, subject, body }), DRAFT_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [lane, type, subject, body, setDraft]);

  const isEmpty = !subject.trim() || !body.trim();
  const canSubmit = !isEmpty && !!lane && !hasUploading && !hasFailed && !createRequest.isPending;

  // Specific, in priority order, so Send never just silently refuses
  // (ADO #542 item 4) — null once everything required is satisfied.
  const disabledReason = !subject.trim()
    ? 'Add a subject'
    : !body.trim()
      ? 'Add some details'
      : !lane
        ? 'Pick where this goes'
        : hasUploading
          ? 'Wait for uploads to finish'
          : // A failed upload never "finishes" — telling the user to wait would
            // leave them staring at a button that is never going to enable.
            hasFailed
            ? 'Remove the failed attachment to continue'
            : null;

  // Captured once per mount/errorRef change, not on every keystroke re-render —
  // page/env/browser don't change mid-compose, and this is the same value
  // shown in the preview card below and sent on submit. `role` is no longer
  // captured client-side — the server resolves the requester's real role and
  // overwrites whatever the client sends (ADO #542 item 8).
  const context = useMemo(() => captureSupportContext(errorRef), [errorRef]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !lane) return;

    setSubmitError(null);
    try {
      const created = await createRequest.mutateAsync({
        lane,
        type,
        subject: subject.trim(),
        body: body.trim(),
        attachments: readyAttachments,
        context,
      });

      clearDraft();
      onSubmitted(created.id);
    } catch {
      setSubmitError("Couldn't send your message. Please try again.");
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {errorRef && (
        <ErrorBanner>🐞 Reporting the error you hit — details attached below.</ErrorBanner>
      )}

      <Label as="div" id="support-lane-label">
        What&apos;s this about?
      </Label>
      {/* Plain toggle buttons, not radio roles — role="radio" promises arrow-key
          navigation between options, which these buttons don't implement. */}
      <Segmented role="group" aria-labelledby="support-lane-label">
        <SegOption
          type="button"
          aria-pressed={lane === 'ACCOUNT'}
          $selected={lane === 'ACCOUNT'}
          onClick={() => handleLaneChange('ACCOUNT')}
        >
          <SegTitle>Account / access</SegTitle>
          <SegSub>Add/manage people</SegSub>
        </SegOption>
        <SegOption
          type="button"
          aria-pressed={lane === 'PLATFORM'}
          $selected={lane === 'PLATFORM'}
          onClick={() => handleLaneChange('PLATFORM')}
        >
          <SegTitle>App problem</SegTitle>
          <SegSub>Bug, billing, question</SegSub>
        </SegOption>
      </Segmented>

      {!lane && <HintNote>Pick one to continue.</HintNote>}

      <RouteHint>
        {lane === 'ACCOUNT' ? (
          <>
            ➡️ Goes to <strong>your Org Admin</strong> — manages accounts &amp; roles.
          </>
        ) : lane === 'PLATFORM' ? (
          <>
            ➡️ Goes to <strong>UpskillABA Support</strong> (support@upskillaba.com).
          </>
        ) : (
          'Pick where this goes.'
        )}
      </RouteHint>

      {/* Shown for either lane once picked (ADO #542 item 3) — options are the
          lane's own catch-all-terminated list, so nothing here can be typed
          that the API would reject as out-of-lane. Hidden entirely while no
          lane is chosen, since there's nothing to scope the options to yet. */}
      {lane && (
        <>
          <Label htmlFor="support-type">Type</Label>
          <Field>
            <Select
              id="support-type"
              value={type}
              onChange={(e) => setType(e.target.value as SupportRequestType)}
            >
              {LANE_SUPPORT_TYPES[lane].map((t) => (
                <option key={t} value={t}>
                  {SUPPORT_TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
        </>
      )}

      <Label htmlFor="support-subject">Subject</Label>
      <Field>
        <TextInput
          id="support-subject"
          type="text"
          placeholder="Short summary"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={140}
          required
        />
      </Field>

      <Label htmlFor="support-body">What happened?</Label>
      <Field onDragOver={(e) => e.preventDefault()} onDrop={handleDropFiles}>
        <TextArea
          id="support-body"
          placeholder="What happened?"
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY_LENGTH))}
          onPaste={handlePasteImage}
          maxLength={MAX_BODY_LENGTH}
          required
        />
      </Field>

      {pending.length > 0 && (
        <AttachRow>
          {pending.map((p) => (
            <PendingAttachmentChip key={p.clientId} attachment={p} onRemove={removeAttachment} />
          ))}
        </AttachRow>
      )}

      <AttachTrigger type="button" onClick={() => fileInputRef.current?.click()}>
        <Paperclip size={12} /> Attach a file
      </AttachTrigger>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) addFiles(Array.from(e.target.files));
          e.target.value = '';
        }}
        accept="image/*,video/*,.pdf,.docx,.xlsx,.doc,.xls"
      />

      <Footer>
        {submitError && <ErrorBanner role="alert">{submitError}</ErrorBanner>}
        <SubmitButton
          type="submit"
          disabled={!canSubmit}
          aria-describedby={disabledReason ? 'support-submit-reason' : undefined}
        >
          {createRequest.isPending ? 'Sending…' : 'Send message'}
        </SubmitButton>
        {/* aria-live so a screen reader announces WHY Send is disabled, not just
            that it is (ADO #542 item 4) */}
        <DisabledReason id="support-submit-reason" role="status" aria-live="polite">
          {disabledReason}
        </DisabledReason>
        <DestNote>
          {lane
            ? lane === 'ACCOUNT'
              ? 'Routed to your Org Admin'
              : 'Routed to UpskillABA Support'
            : 'Pick where this goes above'}
        </DestNote>
      </Footer>
    </Form>
  );
}

export default ComposeView;
